import React, { useEffect, useState } from 'react';

const DEFAULT_TIMEOUT_MS = 15000;

function Spinner({ className = 'h-8 w-8' }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-brand border-t-transparent ${className}`}
      role="status"
      aria-label="Loading"
    />
  );
}

function RefreshButton({ onClick, label = 'Refresh' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-800"
    >
      {label}
    </button>
  );
}

function useLoadTimeout(isPending, timeoutMs) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isPending) {
      setTimedOut(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(timer);
  }, [isPending, timeoutMs]);

  return timedOut;
}

function resolvePending({ isLoading, isFetching, hasData }) {
  return Boolean(isLoading || (isFetching && !hasData));
}

export function isApiPending({ isLoading = false, isFetching = false, hasData = false }) {
  return resolvePending({ isLoading, isFetching, hasData });
}

/**
 * Inline loader / error / timeout state for API-backed content.
 */
export function ApiLoadState({
  isLoading = false,
  isFetching = false,
  hasData = false,
  error = null,
  refetch,
  message = 'Loading...',
  timeoutMs = DEFAULT_TIMEOUT_MS,
  className = '',
  errorMessage = 'Could not load data. Please try again.',
  timeoutMessage = 'This is taking longer than expected.',
}) {
  const isPending = resolvePending({ isLoading, isFetching, hasData });
  const timedOut = useLoadTimeout(isPending, timeoutMs);

  if (error && !isPending) {
    return (
      <div className={`flex flex-col items-center justify-center gap-3 text-center ${className}`}>
        <p className="text-sm text-red-600">{errorMessage}</p>
        {refetch ? <RefreshButton onClick={() => refetch()} /> : null}
      </div>
    );
  }

  if (!isPending) return null;

  if (timedOut) {
    return (
      <div className={`flex flex-col items-center justify-center gap-3 text-center ${className}`}>
        <p className="text-sm text-gray-600">{timeoutMessage}</p>
        {refetch ? <RefreshButton onClick={() => refetch()} /> : null}
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center gap-3 text-center ${className}`}>
      <Spinner />
      <p className="text-sm text-gray-500">{message}</p>
    </div>
  );
}

/**
 * Table row loader / error / timeout state.
 */
export function ApiLoadTableRow({
  colSpan,
  isLoading = false,
  isFetching = false,
  hasData = false,
  error = null,
  refetch,
  message = 'Loading...',
  timeoutMs = DEFAULT_TIMEOUT_MS,
  errorMessage = 'Could not load data. Please try again.',
  timeoutMessage = 'This is taking longer than expected.',
}) {
  const isPending = resolvePending({ isLoading, isFetching, hasData });
  const timedOut = useLoadTimeout(isPending, timeoutMs);

  if (!isPending && !error) return null;

  if (error && !isPending) {
    return (
      <tr>
        <td colSpan={colSpan} className="px-3 py-8 text-center">
          <p className="text-sm text-red-600">{errorMessage}</p>
          {refetch ? (
            <div className="mt-3">
              <RefreshButton onClick={() => refetch()} />
            </div>
          ) : null}
        </td>
      </tr>
    );
  }

  if (isPending && timedOut) {
    return (
      <tr>
        <td colSpan={colSpan} className="px-3 py-8 text-center">
          <p className="text-sm text-gray-600">{timeoutMessage}</p>
          {refetch ? (
            <div className="mt-3">
              <RefreshButton onClick={() => refetch()} />
            </div>
          ) : null}
        </td>
      </tr>
    );
  }

  if (isPending) {
    return (
      <tr>
        <td colSpan={colSpan} className="px-3 py-8 text-center">
          <div className="flex flex-col items-center justify-center gap-3">
            <Spinner />
            <p className="text-sm text-gray-500">{message}</p>
          </div>
        </td>
      </tr>
    );
  }

  return null;
}
